import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Minus } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { scenarioData, tournamentCosts } from './scenarioData';
import { chartBlue, chartGreen, chartRed } from '@/utils/chartColors';

// Format currency
const formatCurrency = (value) => {
  if (value >= 0) return `$${value}M`;
  return `-$${Math.abs(value)}M`;
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  const value = payload[0].value;
  const isPositive = value >= 0;
  
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`text-lg font-bold ${isPositive ? 'text-stable' : 'text-destructive'}`}>
        {formatCurrency(value)}
      </p>
    </div>
  );
};

export const FinancialBreakdown = ({ selectedScenario }) => {
  const scenario = scenarioData[selectedScenario];
  
  // Calculate totals
  const totalValueCreation = 
    scenario.brandValueIncrease + 
    scenario.sponsorshipIncrease + 
    scenario.mediaRightsIncrease + 
    scenario.ticketRevenue + 
    scenario.merchandiseIncrease;
  
  const netROI = totalValueCreation - tournamentCosts.total;
  const roiMultiple = totalValueCreation > 0 ? (totalValueCreation / tournamentCosts.total).toFixed(1) : 0;
  
  // Chart data for revenue breakdown
  const revenueData = [
    { name: 'Brand Value', value: scenario.brandValueIncrease, fill: scenario.brandValueIncrease >= 0 ? chartGreen() : chartRed() },
    { name: 'Sponsorship', value: scenario.sponsorshipIncrease, fill: scenario.sponsorshipIncrease >= 0 ? chartGreen() : chartRed() },
    { name: 'Media Rights', value: scenario.mediaRightsIncrease, fill: chartBlue() },
    { name: 'Ticket Revenue', value: scenario.ticketRevenue, fill: chartBlue() },
    { name: 'Merchandise', value: scenario.merchandiseIncrease, fill: chartBlue() },
  ];
  
  // Cost breakdown data
  const costData = [
    { name: 'Squad Prep', value: tournamentCosts.squadPreparation },
    { name: 'Coaching', value: tournamentCosts.coachingStaff },
    { name: 'Logistics', value: tournamentCosts.logistics },
    { name: 'Camps', value: tournamentCosts.camps },
  ];

  return (
    <section className="mb-10" data-testid="financial-breakdown-section">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-6 h-6 text-stable" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Financial Impact Breakdown</h2>
      </div>
      
      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-5 mb-6">
        {/* Total Value Creation */}
        <Card className={`border-2 ${totalValueCreation >= 0 ? 'border-stable/30 bg-stable/5' : 'border-destructive/30 bg-destructive/5'}`}>
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Value Creation</p>
            <p className={`text-3xl font-bold ${totalValueCreation >= 0 ? 'text-stable' : 'text-destructive'}`} data-testid="total-value">
              {formatCurrency(totalValueCreation)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Gross projected impact</p>
          </CardContent>
        </Card>
        
        {/* Tournament Costs */}
        <Card className="border-border/50 bg-card">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Tournament Costs</p>
            <p className="text-3xl font-bold text-destructive" data-testid="total-costs">
              -${tournamentCosts.total}M
            </p>
            <p className="text-xs text-muted-foreground mt-1">Fixed investment</p>
          </CardContent>
        </Card>
        
        {/* Net ROI */}
        <Card className={`border-2 ${netROI >= 0 ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Net ROI</p>
                <p className={`text-3xl font-bold ${netROI >= 0 ? 'text-primary' : 'text-destructive'}`} data-testid="net-roi">
                  {formatCurrency(netROI)}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-bold ${netROI >= 0 ? 'bg-stable/20 text-stable' : 'bg-destructive/20 text-destructive'}`}>
                {roiMultiple}x
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Return on investment</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown Chart */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-stable" />
              Value Creation Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData} layout="vertical" margin={{ left: 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickFormatter={(v) => `$${v}M`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    width={90}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine x={0} stroke="hsl(var(--border))" />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {revenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Cost Breakdown */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Minus className="w-4 h-4 text-destructive" />
              Tournament Cost Structure
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-4">
              {costData.map((cost, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground">{cost.name}</span>
                      <span className="text-sm font-semibold text-destructive">${cost.value}M</span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-destructive/60 rounded-full"
                        style={{ width: `${(cost.value / tournamentCosts.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 mt-4 border-t border-border/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Total Investment</span>
                  <span className="text-lg font-bold text-destructive">${tournamentCosts.total}M</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default FinancialBreakdown;
